import cn from 'classnames';
import PropTypes from 'prop-types';
import { useRef, useState, useId } from 'react';

import LdsVideoPlayer from '../LdsVideoPlayer/index.jsx';
import ListItem from './ListItem.jsx';
import { timeToSeconds } from '../LdsVideoPlayer/util.js';
import SkipPanel from './SkipPanel.jsx';
import FigureCaption from '../LdsFigureCaption/index.jsx';
export default function LdsVideoListPlayer({
  videoList,
  transcriptMaxHeight,
  transcriptMaxHeightOverride,
  initialVideoId,
  showTranscriptText = 'Show Transcript',
  hideTranscriptText = 'Hide Transcript',
  chapterized = false,
  displayChapterTitle = false,
  className,
  ...rest
}) {
  const [videos, setVideos] = useState(videoList);
  const [activeVideoIndex, setActiveVideoIndex] = useState(
    getInitialVideoIndex(videoList, initialVideoId)
  );
  const [activeVideo, setActiveVideo] = useState(videos[activeVideoIndex]);

  const figureRef = useRef(null);
  const playlistSkipId = useId();
  const transcriptButtonId = useId();

  // youTubePlayerObject has two methods: loadNewVideo and seekToPosition. See
  // useAddYoutubeScript.js for more info
  const [youTubePlayerObject, setYoutubePlayerObject] = useState(null);

  const [seekKalturaFunction, setSeekKalturaFunction] = useState(null);

  function youtubePlayerObjectReady(playerObject) {
    setYoutubePlayerObject(() => playerObject);
  }
  function handleSeekPositionKalturaReady(seekFunction) {
    setSeekKalturaFunction(() => seekFunction);
  }

  function updateActiveVideo(index) {
    scrollActiveVideoIntoView(figureRef);
    setActiveVideoIndex(index);
    const newActiveVideo = videos[index];
    setActiveVideo(newActiveVideo);
    const startTime = timeToSeconds(newActiveVideo.startTime);
    // if the videoId is the same as the active video, the user is trying to
    // access a new chapter in the same video
    if (newActiveVideo.videoId === activeVideo.videoId) {
      if (newActiveVideo.player === 'kaltura') {
        seekKalturaFunction(startTime);
      }
      if (newActiveVideo.player === 'youtube') {
        youTubePlayerObject?.seekToPosition(startTime);
      }
      return;
    }
    if (newActiveVideo.player === 'youtube') {
      youTubePlayerObject?.loadNewVideo(newActiveVideo.videoId);
    }

    setVideos((prevState) => {
      const newState = [...prevState];
      newState[index].autoplay = true;
      return newState;
    });
  }
  const videoPlayerClasses = cn(
    'lds-video-list-player',
    { 'lds-video-list-player-chapterized': chapterized },
    className
  );
  function videoTitleDisplay() {
    if (displayChapterTitle && activeVideo.chapterTitle) {
      return activeVideo.chapterTitle;
    }
    return activeVideo.title;
  }

  return (
    <div
      className={videoPlayerClasses}
      {...rest}
    >
      <SkipPanel
        playlistSkipId={playlistSkipId}
        transcriptButtonId={transcriptButtonId}
      />
      <div className="lds-video-list-player-theatre">
        <FigureCaption
          showDescriptionText={showTranscriptText}
          hideDescriptionText={hideTranscriptText}
          textContent={activeVideo.transcript}
          contentMaxHeight={transcriptMaxHeight}
          contentMaxHeightOverride={transcriptMaxHeightOverride}
          transcriptButtonId={transcriptButtonId}
        >
          <LdsVideoPlayer
            {...activeVideo}
            youtubeObjectReady={youtubePlayerObjectReady}
            seekToPositionKalturaReady={handleSeekPositionKalturaReady}
          />
          <div className="lds-video-list-player-theatre-text clearfix">
            <div className="row">
              <div
                role="status"
                aria-live="polite"
                className="col left title h3"
              >
                {videoTitleDisplay()}
                <span className="visually-hidden">is now playing</span>
              </div>
              <div className="col-auto right duration align-right">
                <span> {activeVideo.duration} </span>
              </div>
            </div>
          </div>
        </FigureCaption>
      </div>
      <ol
        id={playlistSkipId}
        role="list"
        className="lds-video-list-player-playlist row"
      >
        {videos.map((video, index) => {
          return (
            <ListItem
              key={`${video.videoId}-${index}`}
              index={index}
              setActiveVideo={updateActiveVideo}
              chapterized={chapterized}
              activeVideoIndex={activeVideoIndex}
              {...video}
            />
          );
        })}
      </ol>
    </div>
  );
}

LdsVideoListPlayer.propTypes = {
  videoList: PropTypes.arrayOf(
    PropTypes.shape({
      player: PropTypes.string.isRequired,
      videoId: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      duration: PropTypes.string.isRequired,
      transcript: PropTypes.string,
    })
  ).isRequired,
  transcriptMaxHeight: PropTypes.oneOf(['small', 'medium', 'large']),
  transcriptMaxHeightOverride: PropTypes.number,
};

// Utility function to find initial video index
function getInitialVideoIndex(videoList, initialVideoId) {
  if (initialVideoId) {
    const index = videoList.findIndex(
      (video) => video.videoId === initialVideoId
    );
    if (index > -1) {
      return index;
    }
  }
  return 0;
}

function scrollActiveVideoIntoView(figureRef) {
  if (figureRef.current) {
    figureRef.current.scrollIntoView({ behavior: 'smooth' });
  }
}
